<template>
  <fieldset class="annotation-group-selector">
    <legend>Grupos que podrán verla</legend>

    <template v-if="groups.length">
      <div
        v-if="selectedGroups.length"
        class="annotation-selected-groups"
      >
        <span
          v-for="group in selectedGroups"
          :key="group.id"
          class="annotation-group-chip"
        >
          {{ group.name }}
          <button
            type="button"
            :aria-label="'Quitar ' + group.name"
            @click="removeGroup(group.id)"
          >
            ×
          </button>
        </span>
      </div>

      <div
        v-click-outside="closeOptions"
        class="annotation-group-combobox"
      >
        <div
          class="annotation-group-search"
          :class="{ 'annotation-group-search--open': showOptions }"
        >
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            role="combobox"
            autocomplete="off"
            placeholder="Buscar y seleccionar grupos"
            :aria-expanded="showOptions ? 'true' : 'false'"
            @focus="showOptions = true"
            @keydown.esc="closeOptions"
          >

          <button
            type="button"
            aria-label="Mostrar grupos"
            @click="toggleOptions"
          >
            <span :class="{ 'is-open': showOptions }">⌄</span>
          </button>
        </div>

        <div
          v-if="showOptions"
          class="annotation-group-dropdown"
        >
          <div
            v-if="filteredGroups.length"
            class="annotation-group-selector-list"
          >
            <label
              v-for="group in filteredGroups"
              :key="group.id"
              class="annotation-group-option"
            >
              <input
                type="checkbox"
                :value="group.id"
                :checked="isSelected(group.id)"
                @change="toggleGroup(group.id, $event.target.checked)"
              >
              <span>{{ group.name }}</span>
            </label>
          </div>

          <p v-else class="annotation-group-empty">
            No se encontraron grupos para “{{ search }}”.
          </p>

          <div
            v-if="filteredGroups.length"
            class="annotation-group-dropdown-footer"
          >
            <button
              type="button"
              class="annotation-group-results-btn"
              @click="toggleFiltered"
            >
              <span aria-hidden="true">{{ allFilteredSelected ? '−' : '✓' }}</span>
              {{ allFilteredSelected ? 'Quitar visibles' : 'Seleccionar visibles' }}
              ({{ filteredGroups.length }})
            </button>

            <button
              type="button"
              class="annotation-group-done-btn"
              @click="closeOptions"
            >
              Listo
            </button>
          </div>
        </div>
      </div>

      <p class="annotation-group-selection-count">
        {{ selectedCountLabel }}
      </p>
    </template>

    <p v-else class="annotation-group-empty">
      No perteneces a grupos disponibles.
    </p>
  </fieldset>
</template>

<script>
export default {
  name: 'AnnotationGroupSelector',
  props: {
    value: {
      type: Array,
      default: function () {
        return []
      }
    },
    groups: {
      type: Array,
      default: function () {
        return []
      }
    }
  },
  data: function () {
    return {
      search: '',
      showOptions: false
    }
  },
  computed: {
    selectedGroupIds: function () {
      return Array.from(new Set(this.value || []))
    },
    selectedGroups: function () {
      return this.groups.filter(group => this.isSelected(group.id))
    },
    filteredGroups: function () {
      const search = this.normalizeSearch(this.search)
      if (!search) return this.groups
      return this.groups.filter(group => {
        return this.normalizeSearch(group.name).indexOf(search) !== -1
      })
    },
    selectedCountLabel: function () {
      const count = this.selectedGroupIds.length
      return count === 1 ? '1 grupo seleccionado' : count + ' grupos seleccionados'
    },
    allFilteredSelected: function () {
      return this.filteredGroups.length > 0 &&
        this.filteredGroups.every(group => this.isSelected(group.id))
    }
  },
  methods: {
    normalizeSearch: function (value) {
      const rawValue = String(value || '')
      const normalizedValue = rawValue.normalize ? rawValue.normalize('NFD') : rawValue
      return normalizedValue
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },
    isSelected: function (groupId) {
      return this.selectedGroupIds.indexOf(groupId) !== -1
    },
    toggleGroup: function (groupId, selected) {
      const nextValue = this.selectedGroupIds.slice()
      const index = nextValue.indexOf(groupId)
      if (selected && index === -1) nextValue.push(groupId)
      if (!selected && index !== -1) nextValue.splice(index, 1)
      this.$emit('input', nextValue)
    },
    removeGroup: function (groupId) {
      this.toggleGroup(groupId, false)
    },
    toggleFiltered: function () {
      const filteredIds = this.filteredGroups.map(group => group.id)
      if (this.allFilteredSelected) {
        this.$emit('input', this.selectedGroupIds.filter(id => filteredIds.indexOf(id) === -1))
        return
      }
      this.$emit('input', Array.from(new Set(this.selectedGroupIds.concat(filteredIds))))
    },
    toggleOptions: function () {
      this.showOptions = !this.showOptions
      if (this.showOptions) {
        this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus())
      }
    },
    closeOptions: function () {
      this.showOptions = false
      this.search = ''
    }
  }
}
</script>

<style scoped lang="scss">
.annotation-group-selector {
  position: relative;
  min-width: 0;
  margin: 0;
  padding: 12px;

  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: 5px;

  legend {
    padding: 0 5px;
    color: var(--color-navy);
    font-size: 0.84rem;
    font-weight: 700;
  }
}

.annotation-selected-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 9px;
}

.annotation-group-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 4px 7px 4px 9px;

  background: var(--color-fog);
  border: 1px solid var(--color-line);
  border-radius: 14px;

  color: var(--color-navy);
  font-size: 0.76rem;
  font-weight: 600;

  button {
    display: grid;
    width: 17px;
    height: 17px;
    padding: 0;
    place-items: center;

    background: transparent;
    border: 0;
    border-radius: 50%;

    color: var(--color-ghost);
    font: inherit;
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;

    &:hover {
      background: var(--color-white);
      color: var(--color-navy);
    }
  }
}

.annotation-group-combobox {
  position: relative;
}

.annotation-group-search {
  display: flex;
  overflow: hidden;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;

  &:focus-within,
  &--open {
    border-color: var(--color-steel);
    outline: 2px solid var(--color-fog);
    outline-offset: 1px;
  }

  input {
    flex: 1;
    min-width: 0;
    padding: 9px 10px;

    background: transparent;
    border: 0;
    outline: 0;

    color: var(--color-graphite);
    font: inherit;
    font-size: 0.82rem;
  }

  button {
    flex: 0 0 36px;
    width: 36px;
    padding: 0;

    background: transparent;
    border: 0;
    border-left: 1px solid var(--color-line);

    color: var(--color-steel);
    cursor: pointer;

    span {
      display: inline-block;
      font-size: 1.1rem;
      transition: transform 0.15s ease;

      &.is-open {
        transform: rotate(180deg);
      }
    }
  }
}

.annotation-group-dropdown {
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  right: 0;
  left: 0;

  padding: 7px;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;
  box-shadow: 0 8px 18px rgba(14, 42, 64, 0.18);
}

.annotation-group-selector-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 150px;
  overflow-y: auto;
}

.annotation-group-option {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 34px;
  margin: 0;
  padding: 5px 8px;

  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: 4px;

  color: var(--color-graphite);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: var(--color-steel);
  }

  input {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin: 0;
    accent-color: var(--color-orange);
  }
}

.annotation-group-dropdown-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  margin-top: 10px;

  button {
    min-height: 30px;
    padding: 5px 9px;
    border-radius: 4px;
    font: inherit;
    font-size: 0.72rem;
    font-weight: 700;
    cursor: pointer;
  }
}

.annotation-group-results-btn {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  color: var(--color-steel);

  &:hover {
    background: var(--color-fog);
    border-color: var(--color-steel);
  }

  span {
    margin-right: 3px;
    font-size: 0.85rem;
  }
}

.annotation-group-done-btn {
  min-width: 58px;
  background: var(--color-navy);
  border: 1px solid var(--color-navy);
  color: var(--color-white);

  &:hover {
    background: var(--color-steel);
    border-color: var(--color-steel);
  }
}

.annotation-group-selection-count {
  margin: 7px 0 0;
  color: var(--color-ghost);
  font-size: 0.76rem;
}

.annotation-group-empty {
  margin: 5px 3px;
  color: var(--color-ghost);
  font-size: 0.8rem;
}
</style>
